import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Campaign,
  CampaignStats,
  CampaignStatus,
  UpgradeDialogSource,
} from "schemas/dashboard";

import { formatDate } from "utils/time";
import { trackEvent } from "utils/tracking";

import CampaignFilterChips from "../CampaignFilterChips";
import { CampaignStatusChip } from "../CampaignStatusChip";

interface IProps {
  campaign: Campaign;
  stats?: CampaignStats;
  statsLoading: boolean;
  onDelete: (campaign: Campaign) => void;
  onPause: (campaign: Campaign) => void;
  onResume: (campaign: Campaign) => void;
  onOpenAssistant?: (campaign: Campaign) => void;
}

interface MiniStatProps {
  label: string;
  value: React.ReactNode;
}

const MiniStat = ({ label, value }: MiniStatProps) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography
      sx={{
        fontSize: 10,
        color: grey[500],
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: 0.3,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default function CampaignCard({
  campaign,
  stats,
  statsLoading,
  onDelete,
  onPause,
  onResume,
  onOpenAssistant,
}: IProps) {
  const navigate = useNavigate();
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const dash = "—";
  const opensPct = ((stats?.openedPercentage || 0) * 100).toFixed(1);
  const respondedPct = ((stats?.respondedPercentage || 0) * 100).toFixed(1);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        cursor: "pointer",
        transition: "box-shadow 0.15s",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          borderColor: "primary.light",
        },
      }}
      onClick={() => {
        trackEvent("Campaign Card Clicked");
        navigate(`campaign/${campaign.id}`);
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header row: name + delete */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {campaign.name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {onOpenAssistant && (
              <Tooltip
                title="Start AI chat about this campaign"
                placement="top"
              >
                <Button
                  size="small"
                  variant="outlined"
                  disableElevation
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAssistant(campaign);
                  }}
                  startIcon={
                    <Box
                      component="i"
                      className="fa-solid fa-sparkles"
                      sx={{ fontSize: 12, color: "#00875a" }}
                    />
                  }
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1.25,
                    py: 0.25,
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 999,
                    color: "#00875a",
                    backgroundColor: "#dcfce7",
                    borderColor: "#a7f3d0",
                    "&:hover": {
                      backgroundColor: "#bbf7d0",
                      borderColor: "#86efac",
                    },
                  }}
                >
                  AI
                </Button>
              </Tooltip>
            )}
            {campaign.status === CampaignStatus.paused ? (
              <Tooltip title="Resume campaign" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onResume(campaign);
                  }}
                  sx={{
                    flexShrink: 0,
                    color: grey[400],
                    "&:hover": { color: "#00875a" },
                  }}
                >
                  <Box
                    component="i"
                    className="fa-solid fa-play"
                    sx={{ fontSize: 13 }}
                  />
                </IconButton>
              </Tooltip>
            ) : campaign.status === CampaignStatus.in_progress ? (
              <Tooltip
                title="Pause — cancels unsent outreach emails"
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPause(campaign);
                  }}
                  sx={{
                    flexShrink: 0,
                    color: grey[400],
                    "&:hover": { color: "warning.main" },
                  }}
                >
                  <Box
                    component="i"
                    className="fa-solid fa-pause"
                    sx={{ fontSize: 13 }}
                  />
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(campaign);
              }}
              sx={{
                flexShrink: 0,
                color: grey[400],
                "&:hover": { color: "error.main" },
              }}
            >
              <Box
                component="i"
                className="fa-regular fa-trash"
                sx={{ fontSize: 13 }}
              />
            </IconButton>
          </Box>
        </Box>

        {/* Status + date */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 0.75,
            flexWrap: "wrap",
          }}
        >
          {campaign?.status && campaign?.startDate && (
            <CampaignStatusChip
              status={campaign.status}
              startDate={campaign.startDate}
            />
          )}
          {(campaign?.startDate || campaign?.endDate) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                component="i"
                className="fa-regular fa-calendar"
                sx={{ fontSize: 11, color: grey[400] }}
              />
              <Typography sx={{ fontSize: 12, color: grey[600] }}>
                {formatDate(campaign?.startDate)}
                {campaign?.endDate && ` — ${formatDate(campaign.endDate)}`}
              </Typography>
            </Box>
          )}
        </Box>
        <CampaignFilterChips searchParameters={campaign.searchParameters} />
      </Box>

      <Divider />

      {/* Stats row */}
      <Box
        sx={{ display: "flex", justifyContent: "space-around", px: 2, py: 1.5 }}
      >
        <MiniStat label="Brands" value={campaign.campaignBrandCount ?? dash} />
        <MiniStat
          label="Sent"
          value={statsLoading ? dash : stats?.outreachCount ?? dash}
        />
        <MiniStat
          label="Opens"
          value={
            !subscription?.isSubscribed ? (
              <MuiLink
                onClick={(e) => {
                  e.stopPropagation();
                  setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
                }}
                sx={{ fontSize: 12 }}
              >
                Upgrade
              </MuiLink>
            ) : statsLoading ? (
              dash
            ) : (
              `${stats?.openedCount || 0} (${opensPct}%)`
            )
          }
        />
        <MiniStat
          label="Replies"
          value={
            statsLoading
              ? dash
              : `${stats?.respondedCount || 0} (${respondedPct}%)`
          }
        />
      </Box>
    </Card>
  );
}
